import React from "react";
import { css } from "@emotion/css";
import type { ArcGenerationStep } from "../../types/arcGenerationSession";

/**
 * Phase progress strip shown at the top of the arc generation
 * screen.  Communicates the overall arc:
 *
 *   Your Direction →  Quick Calibration  →  Get Your Quest
 *
 * Sits ABOVE the per-question progress bars — bigger-picture phase
 * context belongs higher than step-within-phase detail.
 *
 * Color encodes PROGRESS STATE, not phase identity: completed
 * chips are green, the current chip is the brand primary, upcoming
 * chips are muted grey.  The eye reads "fill from the left" as the
 * learner advances — same affordance every wizard / stepper UI
 * uses, so it doesn't ask the user to learn a new visual mapping.
 *
 * Responsive: at ≤480px viewports the chips tighten (smaller font,
 * less internal padding, narrower gap) and the decorative ✓ / →
 * prefix glyph + ``·`` separator are hidden — state is already
 * communicated by color/fill, so the glyphs become redundant when
 * space is scarce.  The strip fits single-row at 360px wide for
 * the production (3-chip) Open Path flow.
 */

interface OnboardingProgressProps {
  step: ArcGenerationStep;
  /** When false, hides the "Select Learning Area" node — the recipe
   *  doesn't ask the learner to commit to a single domain. */
  hasDomainSelectionStep?: boolean;
  /** When true, the recipe is a module-scoped calibration (no PSI / no
   *  domain): render just "<module> Calibration -> Get Your Quest". */
  calibrationOnly?: boolean;
  /** Module label for the calibration node (e.g. "Transformers"). */
  moduleLabel?: string;
}

interface StepNode {
  label: string;
  /** Steps where this node should be shown as completed. */
  completedAt: ArcGenerationStep[];
  /** Step where this node should be shown as current. */
  currentAt: ArcGenerationStep | null;
}

// ── Phase definitions (in display order) ─────────────────────────

const PSI_STEP: StepNode = {
  label: "Your Direction",
  completedAt: ["domain_recommend", "theta_quiz", "quest_recommend", "quest_confirm"],
  currentAt: "psi",
};

const DOMAIN_SELECTION_STEP: StepNode = {
  label: "Select Learning Area",
  completedAt: ["theta_quiz", "quest_recommend", "quest_confirm"],
  currentAt: "domain_recommend",
};

const PLACEMENT_STEP: StepNode = {
  label: "Quick Calibration",
  completedAt: ["quest_recommend", "quest_confirm"],
  currentAt: "theta_quiz",
};

const QUEST_STEP: StepNode = {
  label: "Get Your Quest",
  completedAt: ["quest_confirm"],
  currentAt: "quest_recommend",
};

// ── Styles ───────────────────────────────────────────────────────

// 480px is a safe breakpoint that catches portrait phones (down to
// 360px) without hitting most tablets.  At and below this width the
// chips tighten + drop decorative glyphs so three labels still
// fit single-row.
const NARROW_BREAKPOINT = "@media (max-width: 480px)";

const wrapperStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
  font-size: 0.78em;

  ${NARROW_BREAKPOINT} {
    gap: 4px;
    font-size: 0.68em;
  }
`;

const nodeBaseStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  white-space: nowrap;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  ${NARROW_BREAKPOINT} {
    /* Tight but still has a bit of padding between the chip border
       and its text (the user-facing constraint).  4 × 8 px keeps
       the label centered and breathable while letting three chips
       fit on a 360px viewport. */
    padding: 4px 8px;
    gap: 4px;
  }
`;

// State styles — reused across every phase so color encodes
// PROGRESS, not identity.

const nodeCompletedStyle = css`
  background: var(--color-success-bg);
  color: var(--color-success-text);
  border: 1px solid var(--color-success-border);
`;

const nodeCurrentStyle = css`
  background: var(--color-primary-muted);
  color: var(--color-primary);
  border: 1px solid var(--color-primary);
  font-weight: 600;
`;

const nodeUpcomingStyle = css`
  background: var(--color-bg-raised);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-subtle);
`;

const separatorStyle = css`
  color: var(--color-text-muted);
  user-select: none;

  ${NARROW_BREAKPOINT} {
    /* The dot separator is decorative — the gap between chips reads
       as separation already.  Drop it to free real-estate. */
    display: none;
  }
`;

const prefixStyle = css`
  /* The ✓ / → glyph before the label.  Hidden on narrow viewports
     because the chip's color already communicates state and we
     need every pixel to fit three labels on a 360px screen. */
  ${NARROW_BREAKPOINT} {
    display: none;
  }
`;

export const OnboardingProgress: React.FC<OnboardingProgressProps> = ({
  step,
  hasDomainSelectionStep = true,
  calibrationOnly = false,
  moduleLabel,
}) => {
  // Module-scoped calibration recipes open straight on placement and end
  // on the quest — there's no Direction/Domain phase to show.
  const calibrationStep: StepNode = {
    label: moduleLabel ? `${moduleLabel} Calibration` : "Calibration",
    completedAt: ["quest_recommend", "quest_confirm"],
    currentAt: "theta_quiz",
  };
  const steps: StepNode[] = calibrationOnly
    ? [calibrationStep, QUEST_STEP]
    : hasDomainSelectionStep
      ? [PSI_STEP, DOMAIN_SELECTION_STEP, PLACEMENT_STEP, QUEST_STEP]
      : [PSI_STEP, PLACEMENT_STEP, QUEST_STEP];
  return (
    <div className={wrapperStyle} data-testid="onboarding-progress">
      {steps.map((node, idx) => {
        const completed = node.completedAt.includes(step);
        const current = node.currentAt === step;
        const state: "completed" | "current" | "upcoming" = completed
          ? "completed"
          : current
            ? "current"
            : "upcoming";
        const stateStyle = completed
          ? nodeCompletedStyle
          : current
            ? nodeCurrentStyle
            : nodeUpcomingStyle;
        return (
          <React.Fragment key={node.label}>
            <span
              className={`${nodeBaseStyle} ${stateStyle}`}
              data-state={state}
              data-testid={`onboarding-progress-step-${node.currentAt ?? "domain"}`}
            >
              {(completed || current) && (
                <span className={prefixStyle} aria-hidden="true">
                  {completed ? "✓" : "→"}
                </span>
              )}
              {node.label}
            </span>
            {idx < steps.length - 1 && (
              <span className={separatorStyle} aria-hidden="true">·</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
